import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
  Box,
  Button,
  Fab,
  List,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import MarkUnreadChatAltIcon from "@mui/icons-material/MarkUnreadChatAlt";
import ChatbotHeader from "./ChatbotHeader";
import ChatbotMessage from "./ChatbotMessage";
import RexLogoDark from "~/core/src/assets/rexLogoDark.png";
import RexLogoLight from "~/core/src/assets/rexLogoLight.png";
import {streamRegulations} from "./api";
import {isValidTextChunk, updateLastMessage} from "@breachrx/streaming";
import {getGreeting} from "./chatbotHelpers";
import {useRegScoutCore} from "../../resources/RegScoutCoreProvider";
import {useTheme} from "../../resources/ConsolidatedThemeProvider";
import {map, isEmpty} from "underscore";
import {useChatbotStyles} from "./chatbotStyles";

const Chatbot = () => {
    const {user} = useRegScoutCore();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const messageListRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [greetingShown, setGreetingShown] = useState(false);
  const [sessionId, setSessionId] = useState();
  const abortControllerRef = useRef(null);
  const {classes} = useChatbotStyles();

  const {activeTheme} = useTheme();
  const isDarkTheme = activeTheme.palette.mode === "dark";
  const logo = isDarkTheme ? RexLogoDark : RexLogoLight;

  const handleSend = async (e) => {
    e.preventDefault();
    if (isEmpty(input) || loading) return;

    const newMessage = {text: input, sender: "user"};
    setMessages((prev) => [...prev, newMessage]);
    setInput("");
    setLoading(true);
    setError("");

    abortControllerRef.current = new AbortController();

    try {
      let fullText = "";
      let lastChunkIndex = -1;
      let currentMessageId = null;
      setMessages((prev) => [...prev, {text: "*Thinking...*", sender: "assistant", isThinking: true}]);

      const stream = streamRegulations(
        {
          prompt: input,
          email: user.email,
          sessionId,
          userName: user.profile?.["custom:customerName"],
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        {authToken: user.IdToken, signal: abortControllerRef.current.signal},
      );

      for await (const chunk of stream) {
        if (chunk.keepalive) continue;

        if (chunk.sessionId && !sessionId) {
          setSessionId(chunk.sessionId);
        }

        if (chunk.messageId && chunk.messageId !== currentMessageId) {
          currentMessageId = chunk.messageId;
        }

        if (isValidTextChunk(chunk, lastChunkIndex)) {
          lastChunkIndex = chunk.chunkIndex;
          fullText += chunk.text;
          updateLastMessage(setMessages, {
            text: fullText,
            sender: "assistant",
            messageId: currentMessageId,
            references: [],
          });
        }

        if (chunk.done && chunk.references) {
          console.log({
            sessionId: chunk.sessionId,
            inputTokens: chunk.inputTokens,
            outputTokens: chunk.outputTokens,
            totalTokens: chunk.totalTokens,
            references: chunk.references,
            toolUseCounts: chunk.toolUseCounts,
            sessionName: chunk.sessionName,
          });
          updateLastMessage(setMessages, {references: chunk.references});
        }

        if (chunk.error) {
          setError(chunk.error);
        }
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        setError("Failed to get response. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    if (!greetingShown) {
      const greetingMessage = getGreeting();
      setMessages((prev) => [...prev, greetingMessage]);
      setGreetingShown(true);
    }
  };

  const handleEndConversation = useCallback(() => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    setSessionId();
    setLoading(false);
    setError("");
    const greetingMessage = getGreeting();
    setMessages([greetingMessage]);
    setGreetingShown(true);
  }, []);

  const handleMinimize = useCallback(() => setOpen(false), []);
  const handleClose = useCallback(() => {
    setOpen(false);
    handleEndConversation();
  }, [handleEndConversation]);

  useEffect(() => {
    if (open && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({behavior: "smooth"});
    }
  }, [messages, open]);

  const inputProps = useMemo(() => ({
    readOnly: loading,
    sx: {
      "& .MuiOutlinedInput-input": {
        whiteSpace: "pre-wrap",
        overflowWrap: "break-word",
      },
    },
  }), [loading]);

  return (
    <>
      <Fab
        color="primary"
        aria-label="chat"
        id="chat-with-rex"
        onClick={handleOpen}
        sx={{position: "fixed", bottom: 50, right: 50}}
      >
        {sessionId ? <MarkUnreadChatAltIcon /> : <ChatIcon />}
      </Fab>
      {open && (
        <Paper elevation={3} className={classes.chatbotPaper}>
          <ChatbotHeader
            classes={classes}
            logo={logo}
            onMinimize={handleMinimize}
            onClose={handleClose}
          />
          <List className={classes.chatbotMessageList} ref={messageListRef}>
            {map(messages, (message, index) => (
              <ChatbotMessage
                message={message}
                messageListRef={messageListRef}
                isStreaming={loading && index === messages.length - 1 && message.sender === "assistant"}
                key={index}
              />
            ))}
            <div ref={messagesEndRef} />
          </List>
          {error && (
            <Typography color="error" align="center">
              {error}
            </Typography>
          )}
          <Box
            component="form"
            onSubmit={handleSend}
            sx={{display: "flex", paddingTop: 2}}
          >
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Type a request..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              multiline
              InputProps={inputProps}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e?.shiftKey) {
                  handleSend(e);
                }
              }}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              id="send-chat"
              disabled={loading || isEmpty(input)}
              sx={{
                marginLeft: 1,
                borderRadius: 0,
              }}
            >
              Send
            </Button>
          </Box>
          <Button
            variant="contained"
            onClick={handleEndConversation}
            id="end-chat"
            color="primary"
            className={classes.chatbotEndButton}
          >
              End Conversation
          </Button>
        </Paper>
      )}
    </>
  );
};

export default Chatbot;
